import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { Link } from 'react-router-dom';
import CostcoMap from './CostcoMap';
import { API_BASE_URL } from '../App';
import {
	fetchPlaceSuggestions,
	filterWarehouses,
	rankWarehousesByDistance,
	requestUserLocation,
} from '../lib/costcoSearch';
import './CostcoChasePage.css';

function CostcoChasePage({ user }) {
	const sessionId = Cookies.get('session');
	const [warehouses, setWarehouses] = useState([]);
	const [game, setGame] = useState(null);
	const [query, setQuery] = useState('');
	const [placeSuggestions, setPlaceSuggestions] = useState([]);
	const [selected, setSelected] = useState(null);
	const [highlightId, setHighlightId] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [locatingNearMe, setLocatingNearMe] = useState(false);
	const [mapCenter, setMapCenter] = useState([-96.5, 39.5]);
	const [mapZoom, setMapZoom] = useState(1);
	const searchRef = useRef(null);

	const loadCatalog = useCallback(() => {
		return fetch(`${API_BASE_URL}/costco-warehouses?country=US`)
			.then((r) => r.json())
			.then((data) => setWarehouses(data.warehouses || []));
	}, []);

	const loadGame = useCallback(() => {
		if (!sessionId) {
			setGame(null);
			return Promise.resolve();
		}
		return fetch(`${API_BASE_URL}/costco-game?country=US`, {
			headers: { Authorization: sessionId },
		})
			.then((r) => r.json())
			.then((data) => {
				if (data.error) throw new Error(data.error);
				setGame(data);
			});
	}, [sessionId]);

	useEffect(() => {
		setLoading(true);
		Promise.all([loadCatalog(), loadGame()])
			.catch((e) => setError(e.message))
			.finally(() => setLoading(false));
	}, [loadCatalog, loadGame]);

	useEffect(() => {
		if (!query.trim() || query.length < 2) {
			setPlaceSuggestions([]);
			return;
		}
		const t = setTimeout(() => {
			fetchPlaceSuggestions(query, { lat: mapCenter[1], lng: mapCenter[0] })
				.then(setPlaceSuggestions)
				.catch(() => setPlaceSuggestions([]));
		}, 280);
		return () => clearTimeout(t);
	}, [query, mapCenter]);

	useEffect(() => {
		requestUserLocation()
			.then(({ lat, lng }) => {
				setMapCenter([lng, lat]);
				setMapZoom(3.2);
			})
			.catch(() => {});
	}, []);

	const visited = useMemo(() => new Set(game?.visitedWarehouseIds || []), [game]);

	const warehouseMatches = useMemo(() => filterWarehouses(warehouses, query), [warehouses, query]);

	const nearbyList = useMemo(() => {
		const base = query.trim() ? warehouseMatches : rankWarehousesByDistance(warehouses, mapCenter[1], mapCenter[0]);
		return base.slice(0, 40);
	}, [warehouseMatches, warehouses, query, mapCenter]);

	const focusWarehouse = (wh, zoom = 5) => {
		if (!wh) return;
		setSelected(wh);
		setHighlightId(wh.id);
		setMapCenter([wh.lng, wh.lat]);
		setMapZoom(zoom);
		setQuery('');
		setPlaceSuggestions([]);
	};

	const focusPlace = (place) => {
		setMapCenter([place.lng, place.lat]);
		setMapZoom(4.5);
		setQuery(place.label);
		setPlaceSuggestions([]);
		const nearest = rankWarehousesByDistance(warehouses, place.lat, place.lng)[0];
		if (nearest) setHighlightId(nearest.id);
	};

	const toggleVisit = async (warehouseId, wantVisited) => {
		if (!sessionId) return;
		const prev = game?.visitedWarehouseIds || [];
		const has = prev.includes(warehouseId);
		if (wantVisited === has) return;
		const next = wantVisited ? [...prev, warehouseId] : prev.filter((id) => id !== warehouseId);
		setSaving(true);
		setError('');
		try {
			const res = await fetch(`${API_BASE_URL}/costco-game`, {
				method: 'PUT',
				headers: {
					Authorization: sessionId,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ visitedWarehouseIds: next, country: 'US' }),
			});
			const data = await res.json();
			if (!res.ok) throw new Error(data.error || data.message || 'Save failed');
			setGame(data);
		} catch (e) {
			setError(e.message);
		} finally {
			setSaving(false);
		}
	};

	const handleLocateMe = () => {
		setLocatingNearMe(true);
		requestUserLocation()
			.then(({ lat, lng }) => {
				setMapCenter([lng, lat]);
				setMapZoom(4);
			})
			.catch(() => {})
			.finally(() => setLocatingNearMe(false));
	};

	const warehouseTotal = game?.progress?.denominator ?? (warehouses.length || null);
	const visitedCount = game?.progress?.count ?? (sessionId ? 0 : null);
	const progressPct =
		game?.progress?.percent ??
		(sessionId && warehouseTotal ? ((visitedCount / warehouseTotal) * 100).toFixed(2) : '0.00');
	const progressBarPct = sessionId && progressPct != null ? progressPct : 0;

	return (
		<div className="costco-shell">
			<section className="costco-hero">
				<div className="costco-progress-card">
					<div className="costco-progress-card__stat">
						<span className="costco-progress-card__num">{visitedCount ?? '—'}</span>
						<span className="costco-progress-card__label">visited</span>
					</div>
					<div className="costco-progress-card__bar-wrap">
						<div className="costco-progress-card__bar" style={{ width: `${Math.min(100, Number(progressBarPct) || 0)}%` }} />
					</div>
					<div className="costco-progress-card__stat costco-progress-card__stat--right">
						<span className="costco-progress-card__num">{warehouseTotal ?? '—'}</span>
						<span className="costco-progress-card__label">US warehouses</span>
					</div>
				</div>
				<p className="costco-hero__pct">{sessionId ? `${progressPct}% complete` : 'Sign in to track your log'}</p>
			</section>

			{error && <p className="costco-error-banner">{error}</p>}

			<div className="costco-main-grid">
				<div className="costco-map-panel">
					<div className="costco-search-bar">
						<input
							ref={searchRef}
							type="search"
							placeholder="Search city, state, or warehouse…"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							aria-label="Search locations and warehouses"
						/>
						<button type="button" className="costco-btn costco-btn--blue" onClick={handleLocateMe} disabled={locatingNearMe}>
							{locatingNearMe ? 'Locating…' : 'Near me'}
						</button>
					</div>
					{(placeSuggestions.length > 0 || (query && warehouseMatches.length > 0)) && (
						<ul className="costco-suggest" role="listbox">
							{warehouseMatches.slice(0, 6).map((w) => (
								<li key={w.id}>
									<button type="button" onClick={() => focusWarehouse(w)}>
										<strong>{w.name}</strong>
										<span>
											{w.city}
											{w.state_code ? `, ${w.state_code}` : ''}
										</span>
									</button>
								</li>
							))}
							{placeSuggestions.map((p, i) => (
								<li key={`place-${i}`}>
									<button type="button" onClick={() => focusPlace(p)}>
										<strong>{p.label}</strong>
										<span>Jump to area</span>
									</button>
								</li>
							))}
						</ul>
					)}
					{loading ? (
						<div className="costco-map-skeleton">Loading map…</div>
					) : (
						<CostcoMap
							visitedIds={game?.visitedWarehouseIds}
							warehouses={warehouses}
							selectedId={selected?.id}
							highlightId={highlightId}
							mapCenter={mapCenter}
							mapZoom={mapZoom}
							onSelectPin={(w) => focusWarehouse(w, 6)}
							onHoverPin={setHighlightId}
						/>
					)}
				</div>

				<aside className="costco-side-panel">
					{selected ? (
						<div className="costco-pin-card">
							<p className="costco-pin-card__eyebrow">Warehouse pin</p>
							<h2>{selected.name}</h2>
							<p className="costco-pin-card__meta">
								{selected.city}
								{selected.state_code ? `, ${selected.state_code}` : ''}
							</p>
							{selected.address && <p className="costco-pin-card__addr">{selected.address}</p>}
							<div className="costco-pin-card__actions">
								{sessionId ? (
									<button
										type="button"
										className={`costco-btn ${visited.has(selected.id) ? 'costco-btn--outline' : 'costco-btn--red'}`}
										disabled={saving}
										onClick={() => toggleVisit(selected.id, !visited.has(selected.id))}
									>
										{visited.has(selected.id) ? 'Remove from my log' : 'Log this visit'}
									</button>
								) : (
									<Link to="/login" className="costco-btn costco-btn--red">
										Sign in to log visits
									</Link>
								)}
								<button type="button" className="costco-btn costco-btn--ghost-dark" onClick={() => setSelected(null)}>
									Close
								</button>
							</div>
							{visited.has(selected.id) && (
								<p className="costco-pin-card__logged">✓ On your lifetime Costco log</p>
							)}
						</div>
					) : (
						<div className="costco-pin-card costco-pin-card--empty">
							<h2>Tap a pin</h2>
							<p>
								Your visit log attaches to warehouse pins — not the shaded territory. Territories
								shift when new stores open (refreshed weekly).
							</p>
						</div>
					)}

					<h3 className="costco-list-heading">{query.trim() ? 'Matching warehouses' : 'Near your map view'}</h3>
					<ul className="costco-list">
						{nearbyList.map((w) => {
							const isVisited = visited.has(w.id);
							return (
								<li key={w.id}>
									<button
										type="button"
										className={`costco-list-item${isVisited ? ' costco-list-item--visited' : ''}${selected?.id === w.id ? ' costco-list-item--active' : ''}`}
										onClick={() => focusWarehouse(w)}
									>
										<span className="costco-list-name">{w.name}</span>
										<span className="costco-list-meta">
											{w.city}
											{w.state_code ? `, ${w.state_code}` : ''}
										</span>
										<span className="costco-list-check">{isVisited ? '✓' : ''}</span>
									</button>
								</li>
							);
						})}
					</ul>
				</aside>
			</div>

			<footer className="costco-footer">
				<p>
					CostcoChase is not affiliated with Costco Wholesale. Warehouse pins from{' '}
					<a href="https://www.alltheplaces.xyz/" rel="noopener noreferrer">
						AllThePlaces
					</a>
					.
				</p>
			</footer>
		</div>
	);
}

export default CostcoChasePage;
